// frontend/src/pages/RecentFiles.tsx - v3.0
// Changes:
// - Replaced fetchAllFiles() + client-side MP4 filter/sort/slice with fetchRecentFiles()
//   hitting /api/listing/recent. The backend returns the 10 most recent MP4s directly,
//   eliminating a full-bucket scan that could return several MB of JSON.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
} from '@mui/material';
import { 
  GetApp as DownloadIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  VideoFile as VideoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  fetchRecentFiles,
  getPresignedDownloadUrl, 
  FileMetadata,
  FileType,
  PresignedUrlResponse,
  formatFileSize,
  getFileName,
} from '../services/api';
import { getDisplayFileName } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';

type Order = 'asc' | 'desc';
type SortField = 'name' | 'lastModified' | 'size';

/**
 * Component to display the most recent files from the S3 bucket
 */
const RecentFiles: React.FC = () => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // For menu
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null); // For menu
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortField>('lastModified');
  const navigate = useNavigate();

  // Function to format date
  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const now = new Date();
      const isThisYear = now.getFullYear() === date.getFullYear();
      return isThisYear
        ? `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`
        : `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  }, []);

  // Function to determine file icon
  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'mp4') {
      return <VideoIcon color="primary" sx={{ mr: 1 }} />;
    }
    return null;
  };

  // Function to handle file download
  const handleDownload = async (event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      setLoading(true);
      recordActivity(file.key, 'downloaded', FileType.VIDEO);
      const response: PresignedUrlResponse = await getPresignedDownloadUrl(file.key);
      console.log('[Debug] Downloading file:', file.key, 'Display name:', getDisplayFileName(file.key));
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || getFileName(file.key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  // Function to handle file playback
  const handlePlay = useCallback((file: FileMetadata) => {
    try {
      console.log('[Debug] Playing file:', file.key, 'Display name:', getDisplayFileName(file.key));
      recordActivity(file.key, 'played', FileType.VIDEO);
      navigate(`/play/${encodeURIComponent(file.key)}`);
    } catch (err) {
      console.error('Error preparing to play file:', err);
      setError('Failed to load file for playback. Please try again.');
      setSnackbarOpen(true);
    }
  }, [navigate]);

  // Function to handle menu click
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('[Debug] Opening menu for file:', file.key, 'Display name:', getDisplayFileName(file.key));
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  // Function to handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  // Function to handle play from menu
  const handlePlayFromMenu = (event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    handlePlay(file);
    handleMenuClose();
  };

  // Fetch the 10 most recent MP4 files from the dedicated backend endpoint.
  // The backend already filters, sorts by LastModified desc, and slices to 10.
  const loadRecentFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching recent files (attempt ${retryCount + 1})`);
      const response = await fetchRecentFiles();
      
      if (response && response.files && Array.isArray(response.files)) {
        setFiles(response.files);
        console.log(`Loaded ${response.files.length} recent files`);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err: any) {
      console.error('Error fetching recent files:', err);
      const errorMessage = err.message || 'Unknown error';
      const statusText = err.response?.statusText || '';
      const statusCode = err.response?.status || '';
      let friendlyError = 'Failed to load recent files. ';
      if (statusCode) friendlyError += `Server returned: ${statusCode} ${statusText}. `;
      if (errorMessage.includes('Network Error')) friendlyError += 'Cannot connect to the server. Please check your connection or try again later.';
      else friendlyError += 'Please try again later.';
      setError(friendlyError);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  // Retry handler
  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
  }, []);

  // Fetch files on mount or retry
  useEffect(() => {
    loadRecentFiles();
  }, [retryCount, loadRecentFiles]);

  // Handle sorting request
  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sorted files for display
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      if (orderBy === 'name') return getDisplayFileName(a.key).localeCompare(getDisplayFileName(b.key)) * (order === 'asc' ? 1 : -1);
      else if (orderBy === 'lastModified') {
        const dateA = a.lastModified ? new Date(a.lastModified).getTime() : -Infinity;
        const dateB = b.lastModified ? new Date(b.lastModified).getTime() : -Infinity;
        return (dateA - dateB) * (order === 'asc' ? 1 : -1);
      } else if (orderBy === 'size') return (a.size - b.size) * (order === 'asc' ? 1 : -1);
      return 0;
    });
  }, [files, order, orderBy]);

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Recent Files</Typography>
        <Button startIcon={<RefreshIcon />} onClick={handleRetry} disabled={loading} variant="outlined">Refresh</Button>
      </Box>
      <Typography variant="body1" paragraph>Showing the 10 most recently uploaded MP4 files.</Typography>
      {loading && files.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={handleRetry} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Retry'}</Button>}>{error}</Alert>
      ) : files.length === 0 ? (
        <Alert severity="info">No recent files found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="recent files table">
            <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '400px' }}><TableSortLabel
                active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}>File Name</TableSortLabel></TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '150px' }}><TableSortLabel
                active={orderBy === 'lastModified'} direction={orderBy === 'lastModified' ? order : 'asc'}
                onClick={() => handleRequestSort('lastModified')}>Last Modified</TableSortLabel></TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '100px' }}><TableSortLabel
                active={orderBy === 'size'} direction={orderBy === 'size' ? order : 'asc'}
                onClick={() => handleRequestSort('size')}>Size</TableSortLabel></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '80px' }}>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>{sortedFiles.map((file) => (
              <TableRow key={file.key} hover onClick={() => handlePlay(file)} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }} style={{ cursor: 'pointer' }}>
                <TableCell component="th" scope="row"><Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getFileIcon(file.key)}<Typography sx={{ color: 'text.primary' }}>{getDisplayFileName(file.key)}</Typography></Box></TableCell>
                <TableCell>{formatDate(file.lastModified)}</TableCell>
                <TableCell>{formatFileSize(file.size)}</TableCell>
                <TableCell align="center"><IconButton size="small" onClick={(e) => handleMenuClick(e, file)}
                  aria-label={`Actions for ${getDisplayFileName(file.key)}`}><MoreVertIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
      )}
      {loading && files.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {selectedFile && (<><MenuItem onClick={(e) => handlePlayFromMenu(e, selectedFile)}><ListItemIcon><PlayIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Play</ListItemText></MenuItem><MenuItem onClick={(e) => handleDownload(e, selectedFile)}><ListItemIcon>
            <DownloadIcon fontSize="small" /></ListItemIcon><ListItemText>Download</ListItemText></MenuItem></>)}
      </Menu>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} message={error} />
    </Box>
  );
};

export default RecentFiles;